'use client';

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

// Delay before showing the loading indicator (ms)
// If navigation completes within this time, no loading indicator is shown
const LOADING_INDICATOR_DELAY_MS = 300;
interface NavigationProgressContextValue {
  isNavigating: boolean;
  startNavigation: () => void;
  endNavigation: () => void;
}
const NavigationProgressContext = createContext<NavigationProgressContextValue | null>(null);
interface NavigationProgressProviderProps {
  children: ReactNode;
}

/**
 * Provider for managing navigation progress state across the application.
 * Automatically ends navigation when pathname or searchParams change.
 *
 * Features:
 * - Debounced loading indicator: Only shows after 300ms delay
 * - Fast navigations (< 300ms) won't show the loading indicator
 */
export const NavigationProgressProvider = ({
  children
}: NavigationProgressProviderProps) => {
  const [isNavigating, setIsNavigating] = useState(false);
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Track if navigation is pending (started but not yet showing indicator)
  const navigationPendingRef = useRef(false);
  const delayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearDelayTimer = useCallback(() => {
    if (delayTimerRef.current) {
      clearTimeout(delayTimerRef.current);
      delayTimerRef.current = null;
    }
  }, []);
  const startNavigation = useCallback(() => {
    // Mark navigation as pending
    navigationPendingRef.current = true;

    // Clear any existing timer
    clearDelayTimer();

    // Only show loading indicator after delay
    delayTimerRef.current = setTimeout(() => {
      // Only show if navigation is still pending
      if (navigationPendingRef.current) {
        setIsNavigating(true);
      }
    }, LOADING_INDICATOR_DELAY_MS);
  }, [clearDelayTimer]);
  const endNavigation = useCallback(() => {
    // Clear pending state and timer
    navigationPendingRef.current = false;
    clearDelayTimer();
    setIsNavigating(false);
  }, [clearDelayTimer]);

  // End navigation when route changes complete
  useEffect(() => {
    endNavigation();
  }, [pathname, searchParams, endNavigation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearDelayTimer();
    };
  }, [clearDelayTimer]);
  const value = useMemo(() => ({
    isNavigating,
    startNavigation,
    endNavigation
  }), [isNavigating, startNavigation, endNavigation]);
  return <NavigationProgressContext.Provider value={value} data-sentry-element="NavigationProgressContext.Provider" data-sentry-component="NavigationProgressProvider" data-sentry-source-file="NavigationProgressContext.tsx">{children}</NavigationProgressContext.Provider>;
};

/**
 * Hook to access navigation progress context.
 * @throws {Error} If used outside of NavigationProgressProvider
 */
export const useNavigationProgressContext = (): NavigationProgressContextValue => {
  const context = useContext(NavigationProgressContext);
  if (!context) {
    throw new Error('useNavigationProgressContext must be used within NavigationProgressProvider');
  }
  return context;
};

/**
 * Safe hook that returns null if used outside of provider (for components that may be used in both contexts).
 */
export const useNavigationProgressContextSafe = (): NavigationProgressContextValue | null => {
  return useContext(NavigationProgressContext);
};